import { createAsyncThunk } from '@reduxjs/toolkit';
import {
  getExpedientQueue,
  getExpedientState,
  postConsumeAlarm,
  postHandleExpedient,
  postParkExpedient,
  postReleaseExpedient,
  postSplitExpedient,
  postTerminateExpedient,
} from 'services/api/endpoints/sac';
import { getExpedient, postExpedientNote, postExpedientResolution, postExpedientWaiting } from 'services/api/endpoints/gdp/expedients';
import {
  clearContextData,
  resetAlertCandidates,
  setAlarmSelected,
  showAlarmResolution,
  showClientSelection,
  toggleAlarmQueue,
} from 'features/alarmQueue/alarmQueueSlice';
import {
  getAlarmQueueAsync,
  getAlertCandidatesAsync,
  loadAlarmContextData,
  postConsumeAlarmAsync,
} from 'features/alarmQueue/alarmQueueThunks';
import { needsClientIdentification } from 'features/alarmQueue/alarmQueueUtils';
import { myExpedientItem } from './expedientQueueUtils';
import { pushNotification } from 'features/notifications/notificationsSlice';
import { i18n } from 'utils';

export const DEFAULT_EXPEDIENT_QUEUE_SIZE = 10;

// SAC credentials are injected by the apiSac request interceptor — thunks
// only need to provide operatorId / payload data.

export const getExpedientQueueAsync = createAsyncThunk('expedientQueue/getExpedientQueue', async (_, { getState }) => {
  const state: any = getState();
  const { id: operatorId } = state.common.user.userLogged;
  const size = state.expedientQueue?.requestSize ?? DEFAULT_EXPEDIENT_QUEUE_SIZE;
  const order = state.expedientQueue?.order ?? 'desc';

  const response = await getExpedientQueue(operatorId, 0, size, order);

  const data = response?.data ?? {};

  return {
    expedients: data.expedients ?? [],
    totalSize: data.totalSize ?? 0,
    operator: data.operator ?? null,
    requestedSize: size,
  };
});

export const postHandleExpedientAsync: any | null = createAsyncThunk(
  'expedientQueue/postHandleExpedient',
  async (data: any, { getState, dispatch }) => {
    const state: any = getState();
    const { id: operatorId } = state.common.user.userLogged;
    const response = await postHandleExpedient(data.expedientId, operatorId);

    if (response?.data.ok) {
      // Misma coreografía que el consume clásico: seleccionar la alerta principal y
      // DESPLEGAR la ventana de atención al momento, sin esperar a que el polling
      // adopte la selección (ese camino solo abre la ventana cuando viene de llamada).
      const mainAlert = data.expedient?.alerts?.[0] ?? null;
      dispatch(clearContextData());
      if (mainAlert) dispatch(setAlarmSelected({ ...mainAlert, alarmTypeSelected: 'alarms' }));
      dispatch(toggleAlarmQueue(true));

      if (mainAlert && needsClientIdentification(mainAlert)) {
        // Número ambiguo: elegir paciente antes de ver datos, como en la cola clásica.
        dispatch(resetAlertCandidates());
        dispatch(getAlertCandidatesAsync(mainAlert.uuid));
        dispatch(showAlarmResolution(false));
        dispatch(showClientSelection(true));
      } else if (mainAlert) {
        dispatch(loadAlarmContextData(mainAlert));
        dispatch(showAlarmResolution(true));
      }
    } else if (response?.data?.messageError === 'ExpedientHeldByOtherOperator') {
      // Exclusividad: otro operador tiene alertas vivas del expediente en mano (cola cargada
      // antes de que el SAC lo ocultara). Se avisa y la cola se refresca abajo.
      dispatch(pushNotification({ message: i18n('expedient.queue.heldByOther'), severity: 'warning' }));
    }

    // Refrescar siempre: si el handle falla porque otro operador se lo llevó,
    // la cola debe reflejarlo igualmente.
    dispatch(getExpedientQueueAsync());
    return response;
  },
);

// Exclusividad del expediente (incidente EU 2026-10-16): las alertas nuevas de un expediente
// que ya tengo en mano son mías —el SAC no se lo enseña a nadie más—. Las que NO son llamada
// entrante se adoptan solas con el consume clásico de cada alerta (pasa a mis working alerts
// sin tocar la selección ni el contexto de la atención en curso: con alerta seleccionada el
// poll no cambia la vista). Las llamadas entrantes esperan al clic: ver answerMyExpedientCallAsync.
export const adoptMyExpedientAlertsAsync: any | null = createAsyncThunk(
  'expedientQueue/adoptMyExpedientAlerts',
  async (data: { expedientId: string; alertIds: string[] }, { getState, dispatch }) => {
    const state: any = getState();
    const { id: operatorId } = state.common.user.userLogged;

    const adopted: string[] = [];
    for (const alertId of data.alertIds) {
      const response = await postConsumeAlarm(operatorId, alertId);
      if (response?.data?.ok) adopted.push(alertId);
    }

    if (adopted.length > 0) dispatch(getAlarmQueueAsync());
    dispatch(getExpedientQueueAsync());
    return { expedientId: data.expedientId, adopted };
  },
);

// Llamada entrante nueva en un expediente mío: no se adopta sola (contestaría sin que nadie
// pulsara). El chip «+N llamadas» de la card de «Mis expedientes» la contesta con el consume
// clásico, que entra en la conferencia y pasa la atención a esa llamada (misma coreografía que
// cambiar el foco desde el timeline del expediente).
export const answerMyExpedientCallAsync: any | null = createAsyncThunk(
  'expedientQueue/answerMyExpedientCall',
  async (data: { alert: any }, { dispatch }) => {
    const result: any = await dispatch(
      postConsumeAlarmAsync({
        alarm: data.alert,
        cardType: 'myAlarms',
        alarmType: `myAlarms${data.alert?.type ?? ''}`,
      }),
    );
    // Working alerts al momento: el timeline deja de pintar la llamada como pendiente (y su
    // botón «Contestar») sin esperar al siguiente poll.
    dispatch(getAlarmQueueAsync());
    dispatch(getExpedientQueueAsync());
    return result?.payload ?? null;
  },
);

// "Mis expedientes": pasar la atención a otro expediente que ya es mío. La ventana lee
// el paciente y el contexto de la alerta seleccionada del alarmQueue, así que cambiar
// solo el expediente seleccionado dejaba la ficha del anterior (o la ventana en blanco
// tras aparcar, sin alerta seleccionada). La selección local cambia al momento y el SAC
// mueve su foco con el HandleAlert de siempre: sobre una alerta ya mía solo enfoca, y el
// consume posterior se rechaza por estado (ok=false esperado, sin tocar la centralita).
export const focusMyExpedientAsync: any | null = createAsyncThunk(
  'expedientQueue/focusMyExpedient',
  async (data: any, { getState, dispatch }) => {
    const mainAlert = data.expedient?.alerts?.[0] ?? null;
    if (!mainAlert) return null;

    const state: any = getState();
    const { id: operatorId } = state.common.user.userLogged;

    dispatch(clearContextData());
    dispatch(setAlarmSelected({ ...mainAlert, alarmTypeSelected: 'myAlarms' }));
    dispatch(toggleAlarmQueue(true));

    if (needsClientIdentification(mainAlert)) {
      dispatch(resetAlertCandidates());
      dispatch(getAlertCandidatesAsync(mainAlert.uuid));
      dispatch(showAlarmResolution(false));
      dispatch(showClientSelection(true));
    } else {
      dispatch(loadAlarmContextData(mainAlert));
      dispatch(showAlarmResolution(true));
    }

    const response = await postConsumeAlarm(operatorId, mainAlert.uuid);
    return response?.data ?? null;
  },
);

export const postParkExpedientAsync: any | null = createAsyncThunk(
  'expedientQueue/postParkExpedient',
  async (data: any, { getState, dispatch }) => {
    const state: any = getState();
    const { id: operatorId } = state.common.user.userLogged;
    const response = await postParkExpedient(data.expedientId, operatorId);
    // Refresco inmediato tras el park: que la verdad post-aparcado llegue antes que
    // cualquier respuesta rezagada del poll (la ventana anti-readopción cubre el resto).
    dispatch(getExpedientQueueAsync());
    return response;
  },
);

// Cancelar la reserva de un expediente (afinidad D9: hijo de un split o aparcado por recarga):
// vuelve a la cola general de todos al momento. El SAC solo suelta las del propio operador.
export const releaseExpedientReservationAsync: any | null = createAsyncThunk(
  'expedientQueue/releaseExpedientReservation',
  async (data: { expedientId: string }, { getState, dispatch }) => {
    const state: any = getState();
    const { id: operatorId } = state.common.user.userLogged;
    const response = await postReleaseExpedient(data.expedientId, operatorId);
    if (response?.data?.ok) {
      dispatch(pushNotification({ message: i18n('expedient.queue.reservation.released'), severity: 'success' }));
    } else {
      dispatch(pushNotification({ message: i18n('expedient.queue.reservation.notReserved'), severity: 'warning' }));
    }
    dispatch(getExpedientQueueAsync());
    return response?.data ?? null;
  },
);

// Split (D9): repetible dentro de la misma atención — las invocaciones siguientes pasan
// el hijo devuelto por la primera para acumular sobre el MISMO expediente nuevo.
export const postSplitExpedientAsync: any | null = createAsyncThunk(
  'expedientQueue/postSplitExpedient',
  async (data: any, { getState }) => {
    const state: any = getState();
    const { id: operatorId } = state.common.user.userLogged;
    const targetExpedientId = data.targetExpedientId ?? state.expedientQueue?.splitChildId ?? undefined;
    const response = await postSplitExpedient(
      data.expedientId,
      operatorId,
      data.alertIds,
      targetExpedientId,
      data.closedEventIds ?? [],
    );
    if (response?.data?.ok === false) {
      throw new Error(response?.data?.messageError ?? 'Split failed');
    }
    return response?.data;
  },
);

// Reanudar la atención tras una recarga (F5): el pagehide aparcó el expediente (D3b no distingue
// recarga de cierre). Se vuelve a coger (handle: consume lo aparcado; si otro se lo llevó entre
// medias, postHandleExpedientAsync avisa) y se devuelve el item de «Mis expedientes» para que
// quien llama lo seleccione y abra la atención. Null si ya no es mío.
export const resumeExpedientAttentionAsync: any | null = createAsyncThunk(
  'expedientQueue/resumeExpedientAttention',
  async (data: { expedientId: string }, { dispatch }) => {
    await dispatch(postHandleExpedientAsync({ expedientId: data.expedientId, expedient: null }));
    const refreshed: any = await dispatch(getAlarmQueueAsync()).unwrap();
    const mine = (refreshed?.myItems ?? []).filter(
      (alarm: any) => (alarm?.expedient?.id ?? alarm?.expedientId) === data.expedientId,
    );
    return mine.length > 0 ? myExpedientItem(data.expedientId, mine) : null;
  },
);

// Estado vivo del expediente en atención (SAC): la lista AUTORITATIVA de sus alertas vivas con
// estado y titular. Es la fuente del timeline de la atención: la foto de la cola solo se
// refresca mientras el expediente tiene pendientes y las working alerts solo traen las mías, y
// cruzar ambas dejaba fantasmas (llamada colgada que volvía a ofrecerse como pendiente, PRE 16/10).
export const getExpedientLiveAlertsAsync: any | null = createAsyncThunk(
  'expedientQueue/getExpedientLiveAlerts',
  async (data: { expedientId: string }, { getState }) => {
    const state: any = getState();
    const { id: operatorId } = state.common.user.userLogged;
    const response = await getExpedientState(data.expedientId, operatorId);
    const payload = response?.data ?? {};
    return {
      expedientId: data.expedientId,
      found: payload.ok !== false && payload.found !== false,
      alerts: payload.alerts ?? [],
      fetchedAt: Date.now(),
    };
  },
);

// Detalle persistente del expediente (gdp): eventos miembros + resolución.
export const getExpedientDetailAsync: any | null = createAsyncThunk(
  'expedientQueue/getExpedientDetail',
  async (data: any) => {
    const response = await getExpedient(data.expedientId);
    if (response?.data?.success === false) {
      throw new Error(response?.data?.errorMessage ?? 'Expedient detail failed');
    }
    return response?.data?.item ?? null;
  },
);

// Resolver el expediente (I5): resolución en gdp (upsert idempotente: reintentar es
// inocuo) → nota de expediente si la hay → terminate en SAC (cierra alertas vivas y el
// expediente). Mismo encadenado que la resolución clásica, sin el guard del 2013: el
// upsert no puede fallar por duplicado.
export const resolveExpedientAsync: any | null = createAsyncThunk(
  'expedientQueue/resolveExpedient',
  async (data: any, { getState }) => {
    const state: any = getState();
    const { id: operatorId } = state.common.user.userLogged;

    const resolutionResponse = await postExpedientResolution({
      expedientId: data.expedientId,
      body: data.resolution,
    });
    if (resolutionResponse?.data?.success === false) {
      throw new Error(resolutionResponse?.data?.errorMessage ?? 'Resolution failed');
    }

    if (data.note && data.note.trim()) {
      const noteResponse = await postExpedientNote({
        expedientId: data.expedientId,
        body: { note: data.note.trim() },
      });
      if (noteResponse?.data?.success === false) {
        throw new Error(noteResponse?.data?.errorMessage ?? 'Note failed');
      }
    }

    const terminateResponse = await postTerminateExpedient(data.expedientId, operatorId);
    return terminateResponse?.data;
  },
);

// Dejar en espera (incidencias-tecnicas F2): como resolver, pero sin resolución. Terminate en SAC (sale de la cola) →
// gdp marca la espera (y quien la deja pasa a seguirlo): sigue abierto en gdp. En ese orden: si el SAC no lo suelta,
// gdp no llega a marcarlo en espera; si falla la espera tras soltarlo, reintentar es seguro (las dos son idempotentes).
// La nota la guarda antes el panel, aparte, para que un reintento no la duplique.
/** Dejar en espera: el SAC lo soltó pero gdp no llegó a apuntar la espera. */
export const HOLD_WAITING_FAILED = 'HOLD_WAITING_FAILED';

export const holdExpedientAsync: any | null = createAsyncThunk(
  'expedientQueue/holdExpedient',
  async (data: any, { getState }) => {
    const state: any = getState();
    const { id: operatorId } = state.common.user.userLogged;

    const terminateResponse = await postTerminateExpedient(data.expedientId, operatorId);
    if (terminateResponse?.data?.ok === false) {
      throw new Error(terminateResponse?.data?.messageError ?? 'Terminate failed');
    }

    // El SAC ya lo soltó: si gdp no apunta la espera, el caso queda cerrado sin resolver y fuera de cola. Se reintenta
    // un par de veces y, si no, se avisa con un error propio para que el panel pida repetir (repetirlo es seguro).
    for (const pause of [0, 1000, 3000]) {
      if (pause) await new Promise((resolve) => setTimeout(resolve, pause));
      try {
        const waitingResponse = await postExpedientWaiting(data.expedientId);
        if (waitingResponse?.data?.success !== false) return terminateResponse?.data;
      } catch (err) {
        // Se reintenta.
      }
    }
    throw new Error(HOLD_WAITING_FAILED);
  },
);
